import type { Exercise } from '../../types.ts';
export const batch2Reading: Exercise[] = [
  {
    id: 'reading-false-alarm',
    category: 'reading',
    title: '1983 Soviet nuclear false alarm incident',
    topic: '1983 Soviet nuclear false alarm incident',
    difficulty: 'Challenging',
    article: {
      title: '1983 Soviet nuclear false alarm incident',
      url: 'https://en.wikipedia.org/wiki/1983_Soviet_nuclear_false_alarm_incident',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-27',
      preview:
        'An early-warning system reported an attack during an unusually tense Cold War period. The article examines the evidence available to the officer on duty and the uncertainty surrounding the decisions that followed.',
      scope: {
        wordCount: 1284,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/1983_Soviet_nuclear_false_alarm_incident',
            wordCount: 131,
            anchors: [''],
          },
          {
            title: 'Background',
            url: 'https://en.wikipedia.org/wiki/1983_Soviet_nuclear_false_alarm_incident#Background',
            wordCount: 536,
            anchors: ['Background'],
          },
          {
            title: 'Incident',
            url: 'https://en.wikipedia.org/wiki/1983_Soviet_nuclear_false_alarm_incident#Incident',
            wordCount: 365,
            anchors: ['Incident'],
          },
          {
            title: 'Aftermath',
            url: 'https://en.wikipedia.org/wiki/1983_Soviet_nuclear_false_alarm_incident#Aftermath',
            wordCount: 252,
            anchors: ['Aftermath'],
          },
        ],
      },
    },
    reading: {
      title: '1983 Soviet nuclear false alarm incident — explanations',
      sections: [
        {
          id: 'judgment',
          title: 'Evaluate a warning against an expected pattern',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Petrov was evaluating a warning, not observing the launches directly. In the article’s account, he expected a US first strike intended to disable Soviet retaliation to involve many missiles. A report of only a few launches therefore conflicted with that expected pattern. This discrepancy contributed to suspicion; it did not logically prove that a small attack was impossible.',
            },
            {
              kind: 'paragraph',
              text: 'The distinction matters when reconstructing a decision under uncertainty. An expectation can make one explanation less plausible without eliminating it. Petrov also questioned the relatively new system’s reliability. His judgment combined several imperfect clues rather than resting on a guarantee supplied by a single observation.',
            },
          ],
        },
        {
          id: 'radar',
          title: 'No confirmation is not always a contradiction',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article explains that land radar could not detect missiles beyond the horizon. A satellite warning and a ground-radar observation therefore did not necessarily cover the same region at the same moment. Lack of immediate radar confirmation could reflect the radar’s limits, rather than showing with certainty that no missile existed.',
            },
            {
              kind: 'paragraph',
              text: 'An independent check becomes informative only when it could reasonably have detected the event being checked. This is why timing and coverage belong in an account of evidence. Waiting can improve corroboration while also reducing the time available to respond; the episode cannot be reduced to choosing a perfectly reliable instrument over a perfectly unreliable one.',
            },
          ],
        },
        {
          id: 'sensor',
          title: 'A repeated signal can share one cause',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The reported false alerts were later attributed to an unusual alignment of sunlight, high-altitude clouds and satellite geometry. The same mechanism could produce multiple apparent launches. Several warnings from the same vulnerable detection process are therefore not equivalent to several independent confirmations.',
            },
            {
              kind: 'paragraph',
              text: 'The article describes a correction involving cross-reference to a geostationary satellite. The relevant principle is diversity of observation: a second perspective may help distinguish a real event from an artifact tied to one geometry. This does not imply that every additional sensor is independent or infallible. Its value depends on whether it supplies information that the original failure mode cannot easily reproduce.',
            },
          ],
        },
        {
          id: 'history',
          title: 'Separate observations from historical inference',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The assigned account explicitly notes disagreement about whether Petrov notified superiors after classifying the detections as false. It also describes a political atmosphere in which leaders feared an attack and might interpret an ambiguous warning aggressively. These are different kinds of information: a disputed action, and contextual evidence about risk.',
            },
            {
              kind: 'paragraph',
              text: 'It is reasonable to describe the incident as dangerous without claiming certainty about an alternative history. A conclusion that war was inevitable would require knowing decisions that were never actually made. A careful reading preserves the distinction between what the system reported, what was subsequently established about the malfunction, and what historians infer might have happened under a different response.',
            },
          ],
        },
      ],
      sources: [
        {
          title: '1983 Soviet nuclear false alarm incident · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/1983_Soviet_nuclear_false_alarm_incident',
          note: 'Assigned external article; adapted factual teaching and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
      authorship:
        'Original questions and adapted explanatory teaching based on the linked article by Wikipedia contributors, CC BY-SA 4.0; prepared with AI assistance.',
    },
    questions: [
      {
        id: 'b2-false-alarm-1',
        prompt:
          'Why did the small number of reported missiles contribute to Petrov’s suspicion in the article’s account?',
        options: [
          {
            id: '0',
            text: 'He believed the satellite could only detect large missiles.',
          },
          {
            id: '1',
            text: 'He expected a disabling first strike to involve many missiles.',
          },
          {
            id: '2',
            text: 'The warning system was incapable of displaying more than five launches.',
          },
          {
            id: '3',
            text: 'The reported count proved the missiles were unarmed.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Petrov expected a first strike to be massive; the small count conflicted with that expectation without proving impossibility.',
        evidence:
          'Reviewed 2026-09-27, Incident: Petrov expected a first strike to be massive; the small count conflicted with that expectation without proving impossibility.',
        sectionIds: ['judgment'],
        sourceSection: {
          title: 'Incident',
          url: 'https://en.wikipedia.org/wiki/1983_Soviet_nuclear_false_alarm_incident#Incident',
        },
      },
      {
        id: 'b2-false-alarm-2',
        prompt:
          'Why was the absence of immediate ground-radar confirmation not a conclusive disproof of the satellite warning?',
        options: [
          {
            id: '0',
            text: 'Radar and satellites intentionally covered different enemy countries.',
          },
          {
            id: '1',
            text: 'Radar confirmation was restricted to launches that officers had already classified as genuine.',
          },
          {
            id: '2',
            text: 'The article says ground radar was entirely offline that night.',
          },
          {
            id: '3',
            text: 'Land radar could not detect missiles beyond the horizon.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The article states the land radar’s horizon limitation, so lack of immediate confirmation had to be interpreted in context.',
        evidence:
          'Reviewed 2026-09-27, Incident: The article states the land radar’s horizon limitation, so lack of immediate confirmation had to be interpreted in context.',
        sectionIds: ['radar'],
        sourceSection: {
          title: 'Incident',
          url: 'https://en.wikipedia.org/wiki/1983_Soviet_nuclear_false_alarm_incident#Incident',
        },
      },
      {
        id: 'b2-false-alarm-3',
        prompt: 'What mechanism was later identified as producing the false alerts?',
        options: [
          {
            id: '0',
            text: 'Sunlight on high clouds aligned unusually with satellite orbits.',
          },
          {
            id: '1',
            text: 'US test missiles were mistakenly identified as armed missiles.',
          },
          {
            id: '2',
            text: 'A deliberate false report was entered by a Soviet officer.',
          },
          {
            id: '3',
            text: 'Ground radar confused aircraft with missile launches.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The investigation identified an unusual sunlight/cloud/satellite alignment as the false-alert cause.',
        evidence:
          'Reviewed 2026-09-27, Incident: The investigation identified an unusual sunlight/cloud/satellite alignment as the false-alert cause.',
        sectionIds: ['sensor'],
        sourceSection: {
          title: 'Incident',
          url: 'https://en.wikipedia.org/wiki/1983_Soviet_nuclear_false_alarm_incident#Incident',
        },
      },
      {
        id: 'b2-false-alarm-4',
        prompt:
          'Which detail does the assigned article explicitly identify as differing between accounts?',
        options: [
          {
            id: '0',
            text: 'Whether the warning system used satellites',
          },
          {
            id: '1',
            text: 'Whether a malfunction was later established',
          },
          {
            id: '2',
            text: 'Whether Petrov notified superiors after judging the alerts false',
          },
          {
            id: '3',
            text: 'Whether the incident occurred during the Cold War',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The Incident section flags conflicting accounts of notification after the detections were judged false.',
        evidence:
          'Reviewed 2026-09-27, Incident: The Incident section flags conflicting accounts of notification after the detections were judged false.',
        sectionIds: ['history'],
        sourceSection: {
          title: 'Incident',
          url: 'https://en.wikipedia.org/wiki/1983_Soviet_nuclear_false_alarm_incident#Incident',
        },
      },
      {
        id: 'b2-false-alarm-5',
        prompt:
          'Why is “several alerts from the same system” weaker than “several independent confirmations” in this episode?',
        options: [
          {
            id: '0',
            text: 'A real launch can generate only one alert.',
          },
          {
            id: '1',
            text: 'Multiple alerts could share the same sensor failure mechanism.',
          },
          {
            id: '2',
            text: 'Only political leaders could count the alerts.',
          },
          {
            id: '3',
            text: 'Repeated warnings automatically cancel one another.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The identified shared optical cause makes repeated alerts compatible with one failure mechanism; this is an inference from the described malfunction.',
        evidence:
          'Reviewed 2026-09-27, Incident: The identified shared optical cause makes repeated alerts compatible with one failure mechanism; this is an inference from the described malfunction.',
        sectionIds: ['sensor'],
        sourceSection: {
          title: 'Incident',
          url: 'https://en.wikipedia.org/wiki/1983_Soviet_nuclear_false_alarm_incident#Incident',
        },
      },
    ],
  },
  {
    id: 'reading-seed-vault',
    category: 'reading',
    title: 'Svalbard Global Seed Vault',
    topic: 'Svalbard Global Seed Vault',
    difficulty: 'Challenging',
    article: {
      title: 'Svalbard Global Seed Vault',
      url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-27',
      preview:
        'What does it take to back up the world’s crop diversity? Read about the vault’s storage design, its relationship with working genebanks, and how ownership and access work.',
      scope: {
        wordCount: 1309,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault',
            wordCount: 136,
            anchors: [''],
          },
          {
            title: 'Construction',
            url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault#Construction',
            wordCount: 302,
            anchors: ['Construction'],
          },
          {
            title: 'Mission',
            url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault#Mission',
            wordCount: 182,
            anchors: ['Mission'],
          },
          {
            title: 'Tripartite agreement',
            url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault#Tripartite_agreement',
            wordCount: 92,
            anchors: ['Tripartite_agreement'],
          },
          {
            title: 'Access to seeds',
            url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault#Access_to_seeds',
            wordCount: 344,
            anchors: ['Access_to_seeds'],
          },
          {
            title: 'Seed storage',
            url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault#Seed_storage',
            wordCount: 149,
            anchors: ['Seed_storage'],
          },
          {
            title: 'Crop Trust',
            url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault#Crop_Trust',
            wordCount: 104,
            anchors: ['Crop_Trust'],
          },
        ],
      },
    },
    reading: {
      title: 'Svalbard Global Seed Vault — explanations',
      sections: [
        {
          id: 'backup',
          title: 'A backup and a working collection serve different roles',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The vault stores duplicates of samples kept by depositing genebanks. A working genebank can conserve and distribute material; the remote backup protects against loss of that working collection. The two roles are complementary rather than interchangeable. Putting duplicates in one vault does not make the original institutions unnecessary.',
            },
            {
              kind: 'paragraph',
              text: 'The article emphasizes ordinary institutional failures as well as spectacular disasters: equipment, funding, accidents and conflict can all interrupt conservation. A backup is valuable before an imagined global catastrophe. The useful comparison is a recoverable copy kept apart from a working collection, rather than a single replacement collection meant to conduct every research and distribution task itself.',
            },
          ],
        },
        {
          id: 'ownership',
          title: 'Custody does not transfer ownership',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The described black-box arrangement separates ownership of the facility from ownership of its contents. Norway owns the facility, but depositing institutions retain rights over their deposited seeds. A deposit is not a transfer of those genetic resources to Norway. Other researchers cannot simply request a depositor’s samples from the vault as if it were an open seed shop.',
            },
            {
              kind: 'paragraph',
              text: 'For normal research access, the article directs users toward the depositing genebanks. This distinction explains how many institutions can share protective infrastructure without surrendering control of their collections. Knowing where material is physically stored is not enough to know who may authorize its release.',
            },
          ],
        },
        {
          id: 'recovery',
          title: 'Recovery includes rebuilding a living collection',
          blocks: [
            {
              kind: 'paragraph',
              text: 'ICARDA’s withdrawals illustrate the purpose of a backup. Conflict made its Syrian genebank unavailable for normal work. Samples retrieved from Svalbard were grown and multiplied in Lebanon and Morocco. Some seed was then returned to the vault and some maintained in the rebuilt working collections.',
            },
            {
              kind: 'paragraph',
              text: 'The sequence matters: retrieve, regenerate, restore both working access and backup coverage. A withdrawal is therefore not evidence that the vault’s preservation mission failed. It can be evidence that the recovery mechanism is being used as intended. Seeds differ from an endlessly copyable digital file because regeneration involves growing living material under suitable conditions.',
            },
          ],
        },
        {
          id: 'storage',
          title: 'Slow biological activity and preserve a fallback',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Low temperature and restricted oxygen reduce metabolic activity and delay seed ageing, according to the article. Mechanical cooling maintains the storage target, while surrounding cold rock and permafrost provide a buffer if power fails. These are layered protections with different roles; natural cold does not mean refrigeration is absent.',
            },
            {
              kind: 'paragraph',
              text: 'The account also distinguishes water intrusion at the entrance from damage to stored seeds. It describes later improvements to address that intrusion. This distinction prevents a dramatic description of a facility problem from silently becoming a different claim about the collection itself. Storage engineering concerns both the normal operating condition and what happens when one supporting system stops working.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Svalbard Global Seed Vault · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault',
          note: 'Assigned external article; adapted factual teaching and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
      authorship:
        'Original questions and adapted explanatory teaching based on the linked article by Wikipedia contributors, CC BY-SA 4.0; prepared with AI assistance.',
    },
    questions: [
      {
        id: 'b2-seed-vault-1',
        prompt: 'Which description best captures the vault’s relationship to depositing genebanks?',
        options: [
          {
            id: '0',
            text: 'It replaces their day-to-day seed distribution.',
          },
          {
            id: '1',
            text: 'It takes ownership so collections can be merged.',
          },
          {
            id: '2',
            text: 'It keeps backup duplicates while working genebanks retain their roles.',
          },
          {
            id: '3',
            text: 'It accepts only seeds no other institution has preserved.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The vault stores duplicates; the depositing genebanks remain working collections and normal access points.',
        evidence:
          'Reviewed 2026-09-27, Mission: The vault stores duplicates; the depositing genebanks remain working collections and normal access points.',
        sectionIds: ['backup'],
        sourceSection: {
          title: 'Mission',
          url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault#Mission',
        },
      },
      {
        id: 'b2-seed-vault-2',
        prompt:
          'Under the article’s black-box arrangement, what happens to ownership when a genebank deposits samples?',
        options: [
          {
            id: '0',
            text: 'Ownership remains with the depositor.',
          },
          {
            id: '1',
            text: 'Ownership transfers to Norway.',
          },
          {
            id: '2',
            text: 'Ownership transfers jointly to all depositors.',
          },
          {
            id: '3',
            text: 'Samples become unrestricted public property.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Access to seeds states that the depositing genebank retains ownership and the sole right of access to its deposited materials.',
        evidence:
          'Reviewed 2026-09-27, Access to seeds: Access to seeds states that the depositing genebank retains ownership and the sole right of access to its deposited materials.',
        sectionIds: ['ownership'],
        sourceSection: {
          title: 'Access to seeds',
          url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault#Access_to_seeds',
        },
      },
      {
        id: 'b2-seed-vault-3',
        prompt: 'Why did ICARDA withdraw samples from the vault?',
        options: [
          {
            id: '0',
            text: 'To relocate all crops permanently to Svalbard',
          },
          {
            id: '1',
            text: 'To demonstrate that the vault sold seeds to researchers',
          },
          {
            id: '2',
            text: 'To replace seeds that had been destroyed inside the vault',
          },
          {
            id: '3',
            text: 'To regenerate collections after conflict disrupted access to its Syrian genebank',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'ICARDA used backup material to regenerate collections in Lebanon and Morocco when the Syrian collection became unavailable.',
        evidence:
          'Reviewed 2026-09-27, Access to seeds: ICARDA used backup material to regenerate collections in Lebanon and Morocco when the Syrian collection became unavailable.',
        sectionIds: ['recovery'],
        sourceSection: {
          title: 'Access to seeds',
          url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault#Access_to_seeds',
        },
      },
      {
        id: 'b2-seed-vault-4',
        prompt:
          'What does the surrounding permafrost contribute alongside mechanical refrigeration?',
        options: [
          {
            id: '0',
            text: 'It eliminates the need for cooling equipment.',
          },
          {
            id: '1',
            text: 'It helps keep the facility cold during an electricity failure.',
          },
          {
            id: '2',
            text: 'It permits seeds to be stored at room temperature.',
          },
          {
            id: '3',
            text: 'It accelerates seed metabolism before planting.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The article describes permafrost as thermal protection if power fails, while mechanical refrigeration maintains storage temperature.',
        evidence:
          'Reviewed 2026-09-27, Seed storage: The article describes permafrost as thermal protection if power fails, while mechanical refrigeration maintains storage temperature.',
        sectionIds: ['storage'],
        sourceSection: {
          title: 'Seed storage',
          url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault#Seed_storage',
        },
      },
      {
        id: 'b2-seed-vault-5',
        prompt:
          'Why is a withdrawal followed by multiplication and redeposit consistent with the vault’s mission?',
        options: [
          {
            id: '0',
            text: 'It proves ordinary genebanks should close.',
          },
          {
            id: '1',
            text: 'It transfers ownership from the depositor to Norway.',
          },
          {
            id: '2',
            text: 'It restores a working collection while renewing its backup.',
          },
          {
            id: '3',
            text: 'It makes the collection accessible to anyone through the vault.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The ICARDA sequence rebuilt working collections and returned some regenerated material to backup storage.',
        evidence:
          'Reviewed 2026-09-27, Access to seeds: The ICARDA sequence rebuilt working collections and returned some regenerated material to backup storage.',
        sectionIds: ['recovery'],
        sourceSection: {
          title: 'Access to seeds',
          url: 'https://en.wikipedia.org/wiki/Svalbard_Global_Seed_Vault#Access_to_seeds',
        },
      },
    ],
  },
  {
    id: 'reading-internet-archive',
    category: 'reading',
    title: 'Internet Archive',
    topic: 'Internet Archive',
    difficulty: 'Challenging',
    article: {
      title: 'Internet Archive',
      url: 'https://en.wikipedia.org/wiki/Internet_Archive',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-27',
      preview:
        'Websites disappear and digital files depend on institutions to keep them usable. This selection introduces the Internet Archive’s preservation infrastructure and the different services built on top of it.',
      scope: {
        wordCount: 1424,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Internet_Archive',
            wordCount: 141,
            anchors: [''],
          },
          {
            title: 'Operations',
            url: 'https://en.wikipedia.org/wiki/Internet_Archive#Operations',
            wordCount: 568,
            anchors: ['Operations', 'Storage_infrastructure', 'Form_990_tax_filings'],
          },
          {
            title: 'Web archiving',
            url: 'https://en.wikipedia.org/wiki/Internet_Archive#Web_archiving',
            wordCount: 565,
            anchors: [
              'Web_archiving',
              'Wayback_Machine',
              'Archive-It',
              'Internet_Archive_Scholar',
              'General_Index',
            ],
          },
          {
            title: 'Items and collections',
            url: 'https://en.wikipedia.org/wiki/Internet_Archive#Items_and_collections',
            wordCount: 150,
            anchors: ['Items_and_collections'],
          },
        ],
      },
    },
    reading: {
      title: 'Internet Archive — explanations',
      sections: [
        {
          id: 'services',
          title: 'Different services answer different needs',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The Wayback Machine lets readers inspect archived versions of websites, including sites that no longer exist. Archive-It serves a different workflow: subscribing institutions select, capture and manage collections they want to preserve. Both concern web preservation, but looking up an old page and operating a curated collecting program are different tasks.',
            },
            {
              kind: 'paragraph',
              text: 'The distinction is useful when choosing a service. A journalist investigating how a published page changed may need historical captures; a library building a continuing local-history archive needs control over a collection. A large archive can support both without implying that every service has the same interface, access arrangement or preservation scope.',
            },
          ],
        },
        {
          id: 'copies',
          title: 'Geographic separation addresses shared failures',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article describes copies of material at distant sites as protection against data loss. Multiple copies on neighboring machines can help with a single disk failure but may remain exposed to a common local disaster. Separating copies geographically can reduce some shared risks.',
            },
            {
              kind: 'paragraph',
              text: 'This is a resilience argument, not a claim of perfect preservation. A replicated deletion or a problem shared across systems could still threaten multiple copies. The assigned text distinguishes storage redundancy from regular backups in its historical account. When interpreting infrastructure claims, ask which failure is being addressed and whether the copies are truly independent with respect to that failure.',
            },
          ],
        },
        {
          id: 'research',
          title: 'Preservation can combine several input routes',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Internet Archive Scholar draws material from web captures, digitized print sources, and uploads or partner collections. These routes matter because scholarship is not available in one uniform format or through one delivery channel. A printed journal scan and an online article may require different collection processes while supporting the same broad goal of access and preservation.',
            },
            {
              kind: 'paragraph',
              text: 'The article distinguishes this service from the General Index, an index over a large collection of journal articles. An index helps locate information; its existence alone does not mean that every indexed work is itself newly scanned or freely downloadable through that index. Service names should be interpreted through their described functions, rather than treated as interchangeable search products.',
            },
          ],
        },
        {
          id: 'organization',
          title: 'Files, items, and collections are different levels',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article describes files as belonging to items, and items as being organized into collections. An item can contain several files plus metadata, such as a description or tags. For example, audio and a cover image can belong to one item. A collection then groups multiple items.',
            },
            {
              kind: 'paragraph',
              text: 'This hierarchy explains why a file count, an item count and a collection count measure different things. Grouping related files preserves their context; metadata helps people find and interpret them. A single item need not be a single file or even a single media type. The example follows the article’s organizational model rather than assuming that every visible page corresponds one-to-one with one stored file.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Internet Archive · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Internet_Archive',
          note: 'Assigned external article; adapted factual teaching and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
      authorship:
        'Original questions and adapted explanatory teaching based on the linked article by Wikipedia contributors, CC BY-SA 4.0; prepared with AI assistance.',
    },
    questions: [
      {
        id: 'b2-internet-archive-1',
        prompt:
          'A historian wants to inspect an earlier version of a website that has since disappeared. Which described service most directly fits?',
        options: [
          {
            id: '0',
            text: 'General Index',
          },
          {
            id: '1',
            text: 'Wayback Machine',
          },
          {
            id: '2',
            text: 'A local book-scanning center',
          },
          {
            id: '3',
            text: 'The Archive’s tax-filing collection',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Wayback Machine provides access to historical website captures, including sites no longer available live.',
        evidence:
          'Reviewed 2026-09-27, Wayback Machine: Wayback Machine provides access to historical website captures, including sites no longer available live.',
        sectionIds: ['services'],
        sourceSection: {
          title: 'Wayback Machine',
          url: 'https://en.wikipedia.org/wiki/Internet_Archive#Wayback_Machine',
        },
      },
      {
        id: 'b2-internet-archive-2',
        prompt:
          'A library wants to choose, capture and manage its own web-preservation collection through a subscription service. Which service matches that workflow?',
        options: [
          {
            id: '0',
            text: 'Archive-It',
          },
          {
            id: '1',
            text: 'Internet Archive Scholar',
          },
          {
            id: '2',
            text: 'General Index',
          },
          {
            id: '3',
            text: 'A Wayback page lookup alone',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Archive-It offers institutional collection selection, capture and management.',
        evidence:
          'Reviewed 2026-09-27, Archive-It: Archive-It offers institutional collection selection, capture and management.',
        sectionIds: ['services'],
        sourceSection: {
          title: 'Archive-It',
          url: 'https://en.wikipedia.org/wiki/Internet_Archive#Archive-It',
        },
      },
      {
        id: 'b2-internet-archive-3',
        prompt:
          'What risk is most directly reduced by keeping copies in geographically distant locations?',
        options: [
          {
            id: '0',
            text: 'The risk that an obsolete file format becomes unreadable',
          },
          {
            id: '1',
            text: 'The risk that a source page was never captured completely',
          },
          {
            id: '2',
            text: 'The risk that rights restrictions limit public access',
          },
          {
            id: '3',
            text: 'The risk that one local disaster affects all copies',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The Operations section explicitly connects distant copies with reducing data-loss risk; geographic separation limits a shared local failure.',
        evidence:
          'Reviewed 2026-09-27, Operations: The Operations section explicitly connects distant copies with reducing data-loss risk; geographic separation limits a shared local failure.',
        sectionIds: ['copies'],
        sourceSection: {
          title: 'Operations',
          url: 'https://en.wikipedia.org/wiki/Internet_Archive#Operations',
        },
      },
      {
        id: 'b2-internet-archive-4',
        prompt:
          'Which combination describes sources of material for Internet Archive Scholar in the assigned reading?',
        options: [
          {
            id: '0',
            text: 'Only newly submitted publisher manuscripts',
          },
          {
            id: '1',
            text: 'Only scanned paper journals',
          },
          {
            id: '2',
            text: 'Web captures, digitized print, and uploads or partner collections',
          },
          {
            id: '3',
            text: 'Only citations copied from General Index',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The Scholar section lists web-identified material, digitized print and upload/partnership sources.',
        evidence:
          'Reviewed 2026-09-27, Internet Archive Scholar: The Scholar section lists web-identified material, digitized print and upload/partnership sources.',
        sectionIds: ['research'],
        sourceSection: {
          title: 'Internet Archive Scholar',
          url: 'https://en.wikipedia.org/wiki/Internet_Archive#Internet_Archive_Scholar',
        },
      },
      {
        id: 'b2-internet-archive-5',
        prompt:
          'An album’s audio files and cover image are stored together with a description and tags. In the article’s terminology, what is that grouping?',
        options: [
          {
            id: '0',
            text: 'A collection that cannot contain other items',
          },
          {
            id: '1',
            text: 'An item that can contain multiple related files',
          },
          {
            id: '2',
            text: 'A single file with no metadata',
          },
          {
            id: '3',
            text: 'A separate data center',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Items can contain multiple mixed-media files and metadata; collections organize multiple items.',
        evidence:
          'Reviewed 2026-09-27, Items and collections: Items can contain multiple mixed-media files and metadata; collections organize multiple items.',
        sectionIds: ['organization'],
        sourceSection: {
          title: 'Items and collections',
          url: 'https://en.wikipedia.org/wiki/Internet_Archive#Items_and_collections',
        },
      },
    ],
  },
  {
    id: 'reading-braess',
    category: 'reading',
    title: "Braess's paradox",
    topic: "Braess's paradox",
    difficulty: 'Challenging',
    article: {
      title: "Braess's paradox",
      url: 'https://en.wikipedia.org/wiki/Braess%27s_paradox',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-27',
      preview:
        'A road improvement can change the decisions made by everyone using the network. This article separates what is attractive to an individual driver from what produces the best overall traffic pattern.',
      scope: {
        wordCount: 1103,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Braess%27s_paradox',
            wordCount: 96,
            anchors: [''],
          },
          {
            title: 'Discovery and definition',
            url: 'https://en.wikipedia.org/wiki/Braess%27s_paradox#Discovery_and_definition',
            wordCount: 337,
            anchors: ['Discovery_and_definition'],
          },
          {
            title: 'Examples',
            url: 'https://en.wikipedia.org/wiki/Braess%27s_paradox#Examples',
            wordCount: 275,
            anchors: ['Examples'],
          },
          {
            title: 'Mathematical approach → Example',
            url: 'https://en.wikipedia.org/wiki/Braess%27s_paradox#Example',
            wordCount: 395,
            anchors: ['Example'],
          },
        ],
      },
    },
    reading: {
      title: "Braess's paradox — explanations",
      sections: [
        {
          id: 'equilibrium',
          title: 'Stable choices need not be collectively best',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A Nash equilibrium is a pattern in which no individual can improve their own outcome by changing only their own choice while the others keep theirs. That is a statement about unilateral incentives, not a guarantee of the smallest total travel time. Drivers may all be acting sensibly given everyone else and still end up in a worse collective pattern.',
            },
            {
              kind: 'paragraph',
              text: 'The distinction depends on what changes together. One driver may be unable to benefit by abandoning a shortcut alone, while coordinated changes by many drivers would benefit everyone. The article’s paradox concerns this gap between stable individual decisions and an overall better assignment, rather than a claim that drivers deliberately choose routes they believe are slower.',
            },
          ],
        },
        {
          id: 'congestion',
          title: 'A route’s attractiveness depends on its users',
          blocks: [
            {
              kind: 'paragraph',
              text: 'In the worked network, some road segments have fixed travel times and others become slower as more travelers use them. A new connection changes the available routes and therefore the incentives that determine congestion. Its own travel time can be tiny while its effect on the rest of the network is substantial.',
            },
            {
              kind: 'paragraph',
              text: 'This is why counting added road length or capacity alone does not settle the outcome. The new route draws traffic onto shared, congestion-sensitive segments. A route that initially looks very fast can lose that advantage as many drivers adopt it, yet remain attractive compared with a unilateral switch under the resulting traffic pattern.',
            },
          ],
        },
        {
          id: 'comparison',
          title: 'Compare equilibria before and after the change',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article’s worked example starts with traffic split between two routes and reports a 65-minute equilibrium trip. After opening the near-zero-time connection, the eventual equilibrium uses the connecting route and takes 80 minutes. The worsening is the difference between these two settled patterns, not the initial experience of the first driver who tries the new link.',
            },
            {
              kind: 'paragraph',
              text: 'A tempting mistake is to freeze everyone else’s original route when judging the road’s effect. That measures one deviation from the old pattern, not the new equilibrium. To evaluate the example, follow the incentive for others to switch and then check whether any individual still benefits from changing routes alone.',
            },
          ],
        },
        {
          id: 'limits',
          title: 'An example establishes possibility, not universality',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Braess’s paradox shows that adding a link can worsen performance under the described decision process. It does not show that every new road is harmful or that closing any road improves traffic. Network layout, demand, congestion response and traveler behavior matter. The article gives examples and modeled cases rather than a universal rule against road construction.',
            },
            {
              kind: 'paragraph',
              text: 'In the worked network, closing the new connection restores the original set of routes and can restore the better split. That is a specific comparison under unchanged demand and travel-time rules. Applying the idea elsewhere requires examining those conditions; a road closure might also change how many trips people make, which is another mechanism discussed in the article’s real-world examples.',
            },
          ],
        },
      ],
      sources: [
        {
          title: "Braess's paradox · Wikipedia contributors",
          url: 'https://en.wikipedia.org/wiki/Braess%27s_paradox',
          note: 'Assigned external article; adapted factual teaching and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
      authorship:
        'Original questions and adapted explanatory teaching based on the linked article by Wikipedia contributors, CC BY-SA 4.0; prepared with AI assistance.',
    },
    questions: [
      {
        id: 'b2-braess-1',
        prompt: 'What does a Nash equilibrium establish in the article’s traffic model?',
        options: [
          {
            id: '0',
            text: 'The total travel time is globally smallest.',
          },
          {
            id: '1',
            text: 'No road experiences congestion.',
          },
          {
            id: '2',
            text: 'No individual driver benefits by changing route alone.',
          },
          {
            id: '3',
            text: 'All drivers have agreed on a coordinated plan.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Equilibrium concerns unilateral incentives; it need not minimize overall travel time.',
        evidence:
          'Reviewed 2026-09-27, Discovery and definition: Equilibrium concerns unilateral incentives; it need not minimize overall travel time.',
        sectionIds: ['equilibrium'],
        sourceSection: {
          title: 'Discovery and definition',
          url: 'https://en.wikipedia.org/wiki/Braess%27s_paradox#Discovery_and_definition',
        },
      },
      {
        id: 'b2-braess-2',
        prompt:
          'How can a near-zero-time new link still worsen travel times in the worked example?',
        options: [
          {
            id: '0',
            text: 'It changes route choices and increases use of congestion-sensitive segments.',
          },
          {
            id: '1',
            text: 'Its own travel time must secretly be 80 minutes.',
          },
          {
            id: '2',
            text: 'Every original road is removed when it opens.',
          },
          {
            id: '3',
            text: 'Drivers stop trying to reduce their travel time.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The link changes incentives and redistributes traffic onto shared variable-time segments.',
        evidence:
          'Reviewed 2026-09-27, Example: The link changes incentives and redistributes traffic onto shared variable-time segments.',
        sectionIds: ['congestion'],
        sourceSection: {
          title: 'Example',
          url: 'https://en.wikipedia.org/wiki/Braess%27s_paradox#Example',
        },
      },
      {
        id: 'b2-braess-3',
        prompt:
          'Why is the first driver’s time saving insufficient to predict the final outcome after the link opens?',
        options: [
          {
            id: '0',
            text: 'The model excludes that driver from the traffic total.',
          },
          {
            id: '1',
            text: 'Only the original routes are allowed at equilibrium.',
          },
          {
            id: '2',
            text: 'The new road’s physical length increases over time.',
          },
          {
            id: '3',
            text: 'Other drivers switch too, changing congestion and travel times.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The initial gain occurs before others adjust; the article tracks subsequent switching to a new equilibrium.',
        evidence:
          'Reviewed 2026-09-27, Example: The initial gain occurs before others adjust; the article tracks subsequent switching to a new equilibrium.',
        sectionIds: ['comparison'],
        sourceSection: {
          title: 'Example',
          url: 'https://en.wikipedia.org/wiki/Braess%27s_paradox#Example',
        },
      },
      {
        id: 'b2-braess-4',
        prompt:
          'In the worked example, what is the increase in equilibrium travel time after the new connection opens?',
        options: [
          {
            id: '0',
            text: '5 minutes',
          },
          {
            id: '1',
            text: '15 minutes',
          },
          {
            id: '2',
            text: '25 minutes',
          },
          {
            id: '3',
            text: '35 minutes',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The reported equilibria are 65 minutes before and 80 after, a difference of 15.',
        evidence:
          'Reviewed 2026-09-27, Example: The reported equilibria are 65 minutes before and 80 after, a difference of 15.',
        sectionIds: ['comparison'],
        sourceSection: {
          title: 'Example',
          url: 'https://en.wikipedia.org/wiki/Braess%27s_paradox#Example',
        },
      },
      {
        id: 'b2-braess-5',
        prompt: 'Which general conclusion is justified by the paradox?',
        options: [
          {
            id: '0',
            text: 'Removing any road improves traffic.',
          },
          {
            id: '1',
            text: 'More capacity always raises travel time.',
          },
          {
            id: '2',
            text: 'Added links can worsen equilibrium performance under some conditions.',
          },
          {
            id: '3',
            text: 'Coordinated routing and self-interested routing always coincide.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The result establishes a possibility under specific network and incentive conditions, not a universal direction for road changes.',
        evidence:
          'Reviewed 2026-09-27, Discovery and definition: The result establishes a possibility under specific network and incentive conditions, not a universal direction for road changes.',
        sectionIds: ['limits'],
        sourceSection: {
          title: 'Discovery and definition',
          url: 'https://en.wikipedia.org/wiki/Braess%27s_paradox#Discovery_and_definition',
        },
      },
    ],
  },
  {
    id: 'reading-molasses',
    category: 'reading',
    title: 'Great Molasses Flood',
    topic: 'Great Molasses Flood',
    difficulty: 'Challenging',
    article: {
      title: 'Great Molasses Flood',
      url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-27',
      preview:
        'A storage-tank failure turned an ordinary industrial material into a neighborhood disaster. Read the account of the event, its investigation, and the distinction between proposed causes and established responsibility.',
      scope: {
        wordCount: 1734,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood',
            wordCount: 112,
            anchors: [''],
          },
          {
            title: 'Background',
            url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood#Background',
            wordCount: 119,
            anchors: ['Background'],
          },
          {
            title: 'Flood',
            url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood#Flood',
            wordCount: 637,
            anchors: ['Flood'],
          },
          {
            title: 'Aftermath',
            url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood#Aftermath',
            wordCount: 424,
            anchors: ['Aftermath', 'Cleanup', 'Fatalities'],
          },
          {
            title: 'Causes',
            url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood#Causes',
            wordCount: 442,
            anchors: ['Causes'],
          },
        ],
      },
    },
    reading: {
      title: 'Great Molasses Flood — explanations',
      sections: [
        {
          id: 'industry',
          title: 'The tank belonged to an industrial supply chain',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The molasses was stored for transfer to a plant producing ethanol, which had uses in beverages and munitions. The tank’s location near the harbor supported unloading from ships and subsequent transfer. It was therefore part of an industrial handling system, rather than simply a reservoir of food awaiting local retail sale.',
            },
            {
              kind: 'paragraph',
              text: 'This context explains why such a large quantity was concentrated at the site. A material’s familiar household use does not describe the scale or hazards of an industrial installation. The question asks about the role recorded in the article, not whether molasses could also be used for other purposes elsewhere.',
            },
          ],
        },
        {
          id: 'warnings',
          title: 'Appearance is not a structural repair',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article describes persistent leakage and reports that the tank was painted brown to conceal it. Concealing visible evidence does not change the mechanical condition producing that evidence. This distinction is central to interpreting the warning signs: the absence of an obvious stain after painting would not establish that the underlying tank had become sound.',
            },
            {
              kind: 'paragraph',
              text: 'The Causes section also describes inadequate testing and later engineering findings about the steel and rivets. Those observations concern construction and evaluation, whereas the paint concerns how the problem appeared. A plausible safety response would need to investigate and address the source of leakage rather than merely make the leakage harder to see.',
            },
          ],
        },
        {
          id: 'responsibility',
          title: 'A proposed explanation is not the investigation’s finding',
          blocks: [
            {
              kind: 'paragraph',
              text: 'After the disaster, the company alleged that anarchists had blown up the tank. The court-appointed auditor instead found the company responsible after extensive hearings. An account of the allegation is therefore not evidence that the allegation was accepted. The sequence distinguishes an interested party’s proposed cause from the reported investigative conclusion.',
            },
            {
              kind: 'paragraph',
              text: 'Historical reading requires preserving who asserted a claim and what later inquiry established. The article discusses multiple possible contributing mechanisms, some with qualified language. It is possible to identify serious construction and testing problems without converting every proposed trigger into a uniquely proven cause or treating the company’s defense as the final result.',
            },
          ],
        },
        {
          id: 'cooling',
          title: 'Distinguish the initial surge from later rescue conditions',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article’s account of the later Harvard study connects warm molasses and lower viscosity with rapid initial movement, then cooling and thickening with difficulty rescuing trapped people. Viscosity describes resistance to flow. A change in temperature can therefore make the same material behave differently across stages of an event.',
            },
            {
              kind: 'paragraph',
              text: 'This explanation concerns a reported contributing mechanism, not a claim that temperature alone accounts for the tank’s failure. Structural condition, stored quantity and the evolving flow are separate parts of the story. The important reading distinction is temporal: evidence about the fast initial wave and evidence about increasingly difficult rescue conditions need not contradict each other.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Great Molasses Flood · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood',
          note: 'Assigned external article; adapted factual teaching and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
      authorship:
        'Original questions and adapted explanatory teaching based on the linked article by Wikipedia contributors, CC BY-SA 4.0; prepared with AI assistance.',
    },
    questions: [
      {
        id: 'b2-molasses-1',
        prompt:
          'What industrial purpose explains the molasses storage described in the Background section?',
        options: [
          {
            id: '0',
            text: 'Storage before transfer to an ethanol plant',
          },
          {
            id: '1',
            text: 'Direct distribution as household heating fuel',
          },
          {
            id: '2',
            text: 'Temporary containment of harbor wastewater',
          },
          {
            id: '3',
            text: 'Cooling the elevated railway',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The tank received molasses for transfer to an ethanol plant; ethanol had beverage and munitions uses.',
        evidence:
          'Reviewed 2026-09-27, Background: The tank received molasses for transfer to an ethanol plant; ethanol had beverage and munitions uses.',
        sectionIds: ['industry'],
        sourceSection: {
          title: 'Background',
          url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood#Background',
        },
      },
      {
        id: 'b2-molasses-2',
        prompt:
          'Why did painting the leaking tank brown fail to address the underlying warning sign?',
        options: [
          {
            id: '0',
            text: 'It increased the required storage capacity.',
          },
          {
            id: '1',
            text: 'It prevented any later investigation.',
          },
          {
            id: '2',
            text: 'It hid leakage without repairing the structure causing it.',
          },
          {
            id: '3',
            text: 'It proved the leaks were harmless.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The account distinguishes concealing visible leakage from repairing or adequately testing the tank.',
        evidence:
          'Reviewed 2026-09-27, Causes: The account distinguishes concealing visible leakage from repairing or adequately testing the tank.',
        sectionIds: ['warnings'],
        sourceSection: {
          title: 'Causes',
          url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood#Causes',
        },
      },
      {
        id: 'b2-molasses-3',
        prompt: 'How did the auditor’s finding compare with the company’s claim about anarchists?',
        options: [
          {
            id: '0',
            text: 'The auditor adopted sabotage as the established cause.',
          },
          {
            id: '1',
            text: 'The auditor found the company responsible instead.',
          },
          {
            id: '2',
            text: 'The auditor declined to investigate responsibility.',
          },
          {
            id: '3',
            text: 'The auditor concluded the tank had never leaked.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The Aftermath section contrasts the sabotage claim with the court-appointed auditor’s finding against the company.',
        evidence:
          'Reviewed 2026-09-27, Aftermath: The Aftermath section contrasts the sabotage claim with the court-appointed auditor’s finding against the company.',
        sectionIds: ['responsibility'],
        sourceSection: {
          title: 'Aftermath',
          url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood#Aftermath',
        },
      },
      {
        id: 'b2-molasses-4',
        prompt:
          'According to the Harvard study described in Causes, why did rescue become harder as the spilled molasses cooled?',
        options: [
          {
            id: '0',
            text: 'Cooling reduced density and spread victims farther apart.',
          },
          {
            id: '1',
            text: 'Cooling kept the liquid moving faster through narrow streets.',
          },
          {
            id: '2',
            text: 'Cooling increased evaporation around trapped victims.',
          },
          {
            id: '3',
            text: 'Cooling increased viscosity and helped trap victims.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The reported study links cooling with thickening and impaired rescue, a different stage from the initial rapid flow.',
        evidence:
          'Reviewed 2026-09-27, Causes: The reported study links cooling with thickening and impaired rescue, a different stage from the initial rapid flow.',
        sectionIds: ['cooling'],
        sourceSection: {
          title: 'Causes',
          url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood#Causes',
        },
      },
      {
        id: 'b2-molasses-5',
        prompt:
          'Which reading best preserves the article’s distinction between evidence and proposed causes?',
        options: [
          {
            id: '0',
            text: 'Every proposed contributing factor is a uniquely proven trigger.',
          },
          {
            id: '1',
            text: 'The company’s allegation has the same status as the auditor’s finding.',
          },
          {
            id: '2',
            text: 'Construction findings and qualified proposed triggers should be distinguished.',
          },
          {
            id: '3',
            text: 'Uncertainty about one trigger means no responsibility was established.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The Causes section uses qualified language for some mechanisms while reporting construction findings; Aftermath separately reports responsibility.',
        evidence:
          'Reviewed 2026-09-27, Causes: The Causes section uses qualified language for some mechanisms while reporting construction findings; Aftermath separately reports responsibility.',
        sectionIds: ['responsibility'],
        sourceSection: {
          title: 'Causes',
          url: 'https://en.wikipedia.org/wiki/Great_Molasses_Flood#Causes',
        },
      },
    ],
  },
];
